'use client';

import { AnimatedIndicator } from '@/components/ui/animated-indicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DEFAULT_CHAT_FILTERS } from '@/lib/chat/constants';
import { ChatBubbleLeftIcon, XMarkIcon } from '@heroicons/react/16/solid';
import {
  ChatGroupFilters,
  ChatGroupsHeader,
  ChatGroupsList,
  ChatGroupsProvider,
  ChatGroupsTitle,
  ChatMessagesGroupActions,
  ChatMessagesHeader,
  ChatMessagesHeaderAvatars,
  ChatMessagesInput,
  ChatMessagesList,
  ChatMessagesProvider,
  ChatMessagesTitle,
  useGetUnreadCount,
  useIsChatInitialized,
} from '@pigello/pigello-chat';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { useState } from 'react';

function UnreadCount() {
  const { groups } = useGetUnreadCount();
  if (!groups || groups.organizationUserGroups.length === 0) {
    return null;
  }
  return <AnimatedIndicator />;
}

export const PigelloChat = () => {
  const [currentGroupKey, setCurrentGroupKey] = useState<string | undefined>(
    undefined
  );
  const [isPigelloChatOpen, setIsPigelloChatOpen] = useState(false);
  const [search, setSearch] = useState('');

  const isInitialized = useIsChatInitialized();

  if (!isInitialized) {
    return null;
  }
  return (
    <Popover
      modal={false}
      open={isPigelloChatOpen}
      onOpenChange={setIsPigelloChatOpen}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button className='relative' variant={'outline'} size={'icon'}>
              <UnreadCount />
              <ChatBubbleLeftIcon className='size-5' />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Pigello Chatt</TooltipContent>
      </Tooltip>
      <PopoverContent
        sideOffset={24}
        className='relative h-[calc(100svh-104px)] max-h-[704px] w-96 rounded-2xl p-0'
        align='start'
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <Button
          onClick={() => setIsPigelloChatOpen(false)}
          type='button'
          variant={'outline'}
          size={'icon'}
          className='absolute -top-2 -right-2 z-20 rounded-full bg-background'
        >
          <XMarkIcon className='size-4' />
        </Button>

        <div className='relative size-full overflow-hidden rounded-2xl'>
          <AnimatePresence initial={false} mode='popLayout'>
            {!currentGroupKey && (
              <motion.div
                key='list'
                initial={{ x: '0' }}
                animate={{ x: '0', opacity: 1 }}
                exit={{ x: '0', opacity: 0.99 }}
                className='absolute inset-0'
                transition={{
                  type: 'tween',
                  duration: 0.2,
                }}
              >
                <ChatGroupsProvider defaultFilters={DEFAULT_CHAT_FILTERS}>
                  <ChatGroupsHeader>
                    <ChatGroupsTitle>Gruppchattar</ChatGroupsTitle>
                  </ChatGroupsHeader>
                  <div className='flex flex-col gap-2 px-4'>
                    <Input
                      placeholder='Sök bland gruppchattar...'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <ChatGroupFilters />
                  </div>
                  <ChatGroupsList
                    hasJoined={true}
                    onGroupSelected={(groupKey) => {
                      setCurrentGroupKey(groupKey);
                    }}
                    currentGroupKey={currentGroupKey}
                  />
                </ChatGroupsProvider>
              </motion.div>
            )}

            {currentGroupKey && (
              <motion.div
                key='chat'
                initial={{ x: '384px' }}
                animate={{ x: '0px' }}
                exit={{ x: '384px' }}
                className='absolute inset-0 z-10 shadow-md'
                transition={{
                  type: 'tween',
                  duration: 0.2,
                }}
              >
                <ChatMessagesProvider
                  groupKey={currentGroupKey}
                  onGroupDeleted={() => {
                    setCurrentGroupKey(undefined);
                  }}
                  className='flex h-full flex-col overflow-hidden bg-[#F7F7FC]'
                >
                  <ChatMessagesHeader className='z-10 flex items-center justify-between gap-2 p-4'>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => {
                        setCurrentGroupKey(undefined);
                      }}
                    >
                      <ArrowLeftIcon className='size-4' />
                    </Button>
                    <ChatMessagesHeaderAvatars />
                    <ChatMessagesTitle />
                    <ChatMessagesGroupActions />
                  </ChatMessagesHeader>
                  <ChatMessagesList />
                  <ChatMessagesInput />
                </ChatMessagesProvider>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PopoverContent>
    </Popover>
  );
};
